import React, { useState, useEffect, forwardRef } from "react";
import { cn } from "@/utils";

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ value, onChange, onBlur, placeholder = "ДД.ММ.ГГГГ", className, disabled = false }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [error, setError] = useState<string | null>(null);

    // Синхронизация с внешним value
    useEffect(() => {
      setDisplayValue(value || "");
    }, [value]);

    const formatDate = (input: string, prevValue: string): string => {
      // Разрешаем только цифры и точки
      const cleaned = input.replace(/[^\d.]/g, "");

      // Если пользователь удаляет символы, не форматируем
      if (cleaned.length < prevValue.length) {
        return cleaned.slice(0, 10);
      }

      // Если пользователь ввёл точку сам, оставляем как есть
      if (cleaned.endsWith(".")) {
        // Не больше 2 точек
        const dotCount = (cleaned.match(/\./g) || []).length;
        if (dotCount <= 2) {
          return cleaned.slice(0, 10);
        }
        return prevValue;
      }

      // Автоформатирование: добавляем точки после 2 и 4 цифр
      const digits = cleaned.replace(/\./g, "");
      let formatted = "";

      for (let i = 0; i < digits.length && i < 8; i++) {
        if (i === 2 || i === 4) {
          formatted += ".";
        }
        formatted += digits[i];
      }

      return formatted;
    };

    const validateDate = (dateStr: string): string | null => {
      if (!dateStr || dateStr.length < 10) {
        return null; // Не валидируем неполную дату
      }

      const parts = dateStr.split(".");
      if (parts.length !== 3) {
        return "Неверный формат даты";
      }

      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      // Проверка дня
      if (day < 1 || day > 31) {
        return "День должен быть от 01 до 31";
      }

      // Проверка месяца
      if (month < 1 || month > 12) {
        return "Месяц должен быть от 01 до 12";
      }

      // Проверка года
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) {
        return `Год должен быть от 1900 до ${currentYear}`;
      }

      // Проверка валидности даты
      const date = new Date(year, month - 1, day);
      if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
        return "Некорректная дата";
      }

      // Проверка что дата не в будущем
      if (date > new Date()) {
        return "Дата не может быть в будущем";
      }

      return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const formatted = formatDate(input, displayValue);

      setDisplayValue(formatted);
      onChange(formatted);

      // Валидация только для полной даты
      if (formatted.length === 10) {
        const validationError = validateDate(formatted);
        setError(validationError);
      } else {
        setError(null);
      }
    };

    const handleBlur = () => {
      if (displayValue && displayValue.length > 0 && displayValue.length < 10) {
        setError("Введите полную дату");
      }
      onBlur?.();
    };

    return (
      <div className="w-full">
        <input
          ref={ref}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10}
          className={cn(
            "block w-full px-3 py-2 border rounded-lg bg-surface-elevated text-fs-sm text-ink-1 placeholder:text-ink-4",
            "transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30",
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/30 bg-red-50/50 dark:border-red-500 dark:bg-red-900/10"
              : "border-line",
            className,
          )}
        />
        {error && <p className="mt-1 text-xs text-error-600 dark:text-error-400">{error}</p>}
      </div>
    );
  },
);

DateInput.displayName = "DateInput";

export default DateInput;
